import { useState, type ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { api } from "../api/client";
import { keys } from "../api/hooks";
import { adminAnalyticsSchema, type AdminAnalytics, type AnalyticsLocationRow } from "../../api/contracts";
import { BarChart, LineChart } from "../components/charts";
import { Button, EmptyState, ErrorNote, PageHeader, Panel, Spinner, Stat, cx } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { StatusDot } from "../components/StatusDot";
import { formatMinutes } from "../lib/format";
import { formatSeconds, formatWaitLabel, shortDay, shortTime } from "../lib/charts";
import { blockLabels, DAYS_OPTIONS, jobSeries, parseDays, pctChange, SKIP_LABELS, sortLocations, tierLabel, type Days, type LocationSortKey } from "../lib/analytics";

export function AnalyticsPage() {
  const [params, setParams] = useSearchParams();
  const days = parseDays(params.get("days"));
  const q = useQuery({ queryKey: keys.admin.analytics(days), queryFn: () => api(`/api/admin/analytics?days=${days}`, adminAnalyticsSchema), placeholderData: keepPreviousData });
  const setDays = (d: Days) => setParams({ days: String(d) });
  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle={q.data?.mode === "shadow" ? "How much the app is doing, and whether that is changing. Preview mode: block counts are what would have happened." : "How much the app is doing, and whether that is changing."}
        actions={<div role="group" aria-label="Range" className="flex gap-1">{DAYS_OPTIONS.map((d) => <Button key={d} size="sm" variant={d === days ? "primary" : "secondary"} onClick={() => setDays(d)}>{d} days</Button>)}</div>}
      />
      {q.isPending && <Spinner />}
      {q.isError && <ErrorNote error={q.error} />}
      {q.data && <Body a={q.data} />}
    </>
  );
}

/** `noEarlierData`: the previous range has no events or jobs at all (the app was not running yet), so there is nothing to compare. */
function StatTile({ label, value, previous, days, badIfUp, noEarlierData, format = (v) => v.toLocaleString() }: { label: string; value: number; previous: number; days: number; badIfUp?: boolean; noEarlierData: boolean; format?: (v: number) => string }) {
  const pct = noEarlierData ? null : pctChange(value, previous);
  const none = `None in the previous ${days} days`;
  if (noEarlierData) return <Stat label={label} value={format(value)} detail="No earlier data to compare" />;
  if (pct === null) return <Stat label={label} value={format(value)} delta={none} deltaTone={badIfUp && value > 0 ? "danger" : "neutral"} />;
  const tone = pct === 0 || !badIfUp ? "neutral" : pct > 0 ? "danger" : "success";
  return <Stat label={label} value={format(value)} delta={pct > 0 ? `+${pct}%` : `${pct}%`} deltaTone={tone} />;
}

function Chart({ title, caption, empty, children }: { title: string; caption: string; empty: boolean; children: ReactNode }) {
  return (
    <Panel title={title}>
      <p className="mb-3 text-xs text-hs-text-muted">{caption}</p>
      {empty ? <EmptyState title="Nothing recorded in this range" /> : children}
    </Panel>
  );
}

function Breakdown({ title, rows, format = (v) => v.toLocaleString() }: { title: string; rows: { label: string; count: number }[]; format?: (v: number) => string }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Panel title={title}>
      {rows.length === 0 ? <EmptyState title="Nothing recorded in this range" /> : (
        <ul className="space-y-2 text-sm">
          {rows.map((r) => (
            <li key={r.label}>
              <div className="flex justify-between"><span>{r.label}</span><span className="text-hs-text-muted tabular-nums">{format(r.count)}</span></div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-hs-surface-alt"><div className="h-1.5 rounded-full bg-hs-accent" style={{ width: `${(r.count / max) * 100}%` }} /></div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

function Body({ a }: { a: AdminAnalytics }) {
  const t = a.totals, x = a.daily.map((d) => d.date), labels = blockLabels(a.mode);
  const nothing = (...cols: number[][]) => cols.every((c) => sum(c) === 0);
  const events = a.daily.map((d) => d.events), failed = a.daily.map((d) => d.failedJobs), writeErrors = a.daily.map((d) => d.writeErrors);
  const created = a.daily.map((d) => d.created), removed = a.daily.map((d) => d.removed), resized = a.daily.map((d) => d.resized);
  const p50 = a.daily.map((d) => d.replanDurationMs?.p50 ?? null), p95 = a.daily.map((d) => d.replanDurationMs?.p95 ?? null);
  const jobs = jobSeries(a.daily);
  const noEarlierData = t.previous.events === 0 && t.previous.jobs === 0;
  const qx = a.queue.map((s) => s.sampledAt);
  const queueTitle = a.days === 7 ? shortTime : shortDay;
  const queueCaption = a.days === 7 ? "How long the oldest due job had waited, sampled every 15 minutes." : "Longest wait of the oldest due job each day.";
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Webhook events" value={t.current.events} previous={t.previous.events} days={a.days} noEarlierData={noEarlierData} />
        <StatTile label="Jobs" value={t.current.jobs} previous={t.previous.jobs} days={a.days} noEarlierData={noEarlierData} />
        <StatTile label="Failed jobs" value={t.current.failedJobs} previous={t.previous.failedJobs} days={a.days} badIfUp noEarlierData={noEarlierData} />
        <StatTile label="Boulevard write errors" value={t.current.writeErrors} previous={t.previous.writeErrors} days={a.days} badIfUp noEarlierData={noEarlierData} />
      </div>
      <BlocksSection a={a} noEarlierData={noEarlierData} />
      <Chart title="Webhook events per day" caption="Boulevard appointment deliveries, fleet-wide." empty={nothing(events)}>
        <BarChart title="Webhook events per day" x={x} xTick={shortDay} series={[{ label: "Events", values: events }]} />
      </Chart>
      <Chart title="Jobs per day" caption="Finished jobs by the day they finished, stacked by type. Cancelled jobs are left out." empty={nothing(...jobs.map((s) => s.values as number[]))}>
        <BarChart title="Jobs per day" x={x} xTick={shortDay} stacked series={jobs} />
      </Chart>
      <Chart title="Webhook re-plan duration" caption="How long a location-day re-plan took, median and 95th percentile." empty={p50.every((v) => v === null)}>
        <LineChart title="Webhook re-plan duration" x={x} xTick={shortDay} series={[{ label: "Median", values: p50 }, { label: "95th percentile", values: p95 }]} format={formatSeconds} />
      </Chart>
      <Chart title="Webhook work waiting" caption={queueCaption} empty={a.queue.length === 0}>
        <LineChart title="Webhook work waiting" x={qx} xTick={shortDay} xTitle={queueTitle}
          series={[{ label: "Oldest due webhook job", values: a.queue.map((s) => (s.priorityOldestWaitS ?? 0) / 60) }]}
          references={[{ value: 10, label: "alert at 10 min" }]} format={formatWaitLabel} />
      </Chart>
      <Chart title="Nightly and rules re-plans waiting" caption={queueCaption} empty={a.queue.length === 0}>
        <LineChart title="Nightly and rules re-plans waiting" x={qx} xTick={shortDay} xTitle={queueTitle}
          series={[{ label: "Oldest due re-plan", values: a.queue.map((s) => (s.backgroundOldestWaitS ?? 0) / 60) }]}
          references={[{ value: 240, label: "alert at 4 h" }]} format={formatWaitLabel} />
      </Chart>
      <Chart title="BTB blocks per day" caption={a.mode === "live" ? "Blocks this system placed, removed and resized on Boulevard calendars." : "What the planner would have done. Only changed decisions are counted."} empty={nothing(created, removed, resized)}>
        <BarChart title="BTB blocks per day" x={x} xTick={shortDay} series={[{ label: labels.created, values: created }, { label: labels.removed, values: removed }, { label: labels.resized, values: resized }]} />
      </Chart>
      <Chart title="Failures per day" caption="Jobs that exhausted their attempts, and live Boulevard writes that failed." empty={nothing(failed, writeErrors)}>
        <BarChart title="Failures per day" x={x} xTick={shortDay} series={[{ label: "Failed jobs", values: failed }, { label: "Write errors", values: writeErrors }]} />
      </Chart>
      <div className="grid gap-4 md:grid-cols-2">
        <Breakdown title="Why nothing changed" rows={a.skips.map((s) => ({ label: SKIP_LABELS[s.category], count: s.count }))} />
        <Breakdown title={a.mode === "live" ? "Tier mix of placed blocks" : "Tier mix of blocks that would be placed"} rows={a.tiers.map((t) => ({ label: tierLabel(t.tierIndex), count: t.count }))} />
      </div>
      <LocationTable rows={a.locations} labels={labels} mode={a.mode} />
    </div>
  );
}

/**
 * Blocks placed and the minutes they cover, for the range, fleet-wide and by location. Time is summed
 * from the managed block each create wrote, so Preview mode (which writes nothing) has none to show.
 */
function BlocksSection({ a, noEarlierData }: { a: AdminAnalytics; noEarlierData: boolean }) {
  const t = a.totals, live = a.mode === "live";
  const byLocation = (key: "created" | "placedMinutes") =>
    a.locations.filter((l) => l[key] > 0).sort((x, y) => y[key] - x[key] || x.name.localeCompare(y.name)).map((l) => ({ label: l.name, count: l[key] }));
  return (
    <section aria-labelledby="blocks-heading" className="space-y-4">
      <div>
        <h2 id="blocks-heading" className="text-sm font-semibold text-hs-text">BTB blocks</h2>
        <p className="text-xs text-hs-text-muted">
          {live ? `Blocks this system placed in the last ${a.days} days, and how much calendar time they covered.` : `What the planner would have placed in the last ${a.days} days. Preview mode writes nothing, so there is no blocked time to add up.`}
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile label={live ? "Blocks placed" : "Blocks that would be placed"} value={t.current.blocksCreated} previous={t.previous.blocksCreated} days={a.days} noEarlierData={noEarlierData} />
        <StatTile label={live ? "Blocks removed" : "Blocks that would be removed"} value={t.current.blocksRemoved} previous={t.previous.blocksRemoved} days={a.days} noEarlierData={noEarlierData} />
        {live
          ? <StatTile label="Time blocked" value={t.current.blockedMinutes} previous={t.previous.blockedMinutes} days={a.days} noEarlierData={noEarlierData} format={formatMinutes} />
          : <Stat label="Time blocked" value="—" detail="Not recorded in Preview" />}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Breakdown title={live ? "Blocks placed by location" : "Blocks that would be placed by location"} rows={byLocation("created")} />
        {live && <Breakdown title="Time blocked by location" rows={byLocation("placedMinutes")} format={formatMinutes} />}
      </div>
    </section>
  );
}

const COLUMNS: { key: LocationSortKey; label: (l: ReturnType<typeof blockLabels>) => string; minutes?: boolean; liveOnly?: boolean }[] = [
  { key: "events", label: () => "Events" }, { key: "jobs", label: () => "Jobs" }, { key: "failedJobs", label: () => "Failed" },
  { key: "created", label: (l) => l.created }, { key: "placedMinutes", label: () => "Time placed", minutes: true, liveOnly: true },
  { key: "removed", label: (l) => l.removed }, { key: "writeErrors", label: () => "Write errors" },
  { key: "activeBlocks", label: () => "Active blocks" }, { key: "blockedMinutes", label: () => "Upcoming blocked time", minutes: true },
];

function LocationTable({ rows, labels, mode }: { rows: AnalyticsLocationRow[]; labels: ReturnType<typeof blockLabels>; mode: "shadow" | "live" }) {
  const [sort, setSort] = useState<{ key: LocationSortKey; dir: "asc" | "desc" }>({ key: "events", dir: "desc" });
  const click = (key: LocationSortKey) => setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "name" ? "asc" : "desc" }));
  const sorted = sortLocations(rows, sort.key, sort.dir);
  const n = (v: number, f: (v: number) => string = (v) => v.toLocaleString()) => (v === 0 ? <span className="text-hs-text-subtle">—</span> : <span>{f(v)}</span>);
  const head = (key: LocationSortKey, label: string) => <button type="button" onClick={() => click(key)} className={cx("font-medium", sort.key === key && "text-hs-text underline")}>{label}</button>;
  const sortOf = (key: LocationSortKey) => (sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined);
  const columns: Column<AnalyticsLocationRow>[] = [
    { key: "name", header: head("name", "Location"), sort: sortOf("name"), cell: (r) => <div><span data-testid="loc-name">{r.name}</span><p className="text-xs text-hs-text-muted">{r.ownerIdentifier}</p></div> },
    { key: "state", header: "State", width: "8rem", cell: (r) => r.windingDown ? <StatusDot tone="warning" label="Removing" /> : r.paused ? <StatusDot tone="warning" label="Paused" /> : r.enabled ? <StatusDot tone="success" label="On" /> : <StatusDot tone="neutral" label="Off" /> },
    ...COLUMNS.filter((c) => !c.liveOnly || mode === "live").map((c): Column<AnalyticsLocationRow> => ({
      key: c.key, header: head(c.key, c.label(labels)), sort: sortOf(c.key), align: "right",
      cell: (r) => (c.minutes ? <span className="whitespace-nowrap">{n(r[c.key as "placedMinutes" | "blockedMinutes"], formatMinutes)}</span> : n(r[c.key as Exclude<LocationSortKey, "name">])),
    })),
  ];
  return (
    <Panel padding="none">
      <DataTable columns={columns} rows={sorted} rowKey={(r) => r.blvdId} density="compact" stickyHeader caption="Per location" />
    </Panel>
  );
}
